import { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Icon, Loading, Button } from '@evertrue/et-components';
import EverTrue from 'app';
import cadenceGqlCall from '../../utils/cadence-gql-call';
import {
  CADENCE_QUERY,
  CADENCE_STATS_QUERY,
  CADENCE_STEP_STATS_QUERY,
  CADENCE_MEMBERS_QUERY,
  CADENCE_MEMBER_QUERY,
  CADENCE_GOAL_PROGRESS_QUERY,
  JOBS_QUERY,
  RECENT_CADENCE_ACTIVITY_QUERY,
  CADENCE_MEMBER_ACTIVITY_QUERY,
  REEVALUATE_SUCCESS_METRICS_MUTATION,
  RECALCULATE_CADENCE_STATS_MUTATION,
  RETRY_BLOCKED_MEMBER_MUTATION,
} from '../../utils/cadence-queries';

const SECTION_STYLE = {
  background: '#1e1e2e',
  border: '1px solid #313244',
  borderRadius: 8,
  marginBottom: 16,
  overflow: 'hidden',
};

const HEADER_STYLE = {
  padding: '10px 16px',
  background: '#313244',
  color: '#cdd6f4',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  fontFamily: 'monospace',
  fontSize: 13,
  fontWeight: 600,
  userSelect: 'none',
};

const BODY_STYLE = {
  padding: 16,
  fontFamily: 'monospace',
  fontSize: 12,
  lineHeight: 1.5,
  color: '#a6adc8',
  overflow: 'auto',
  maxHeight: 500,
};

const BADGE_STYLE = {
  display: 'inline-block',
  padding: '2px 8px',
  borderRadius: 4,
  fontSize: 11,
  fontWeight: 600,
  marginLeft: 8,
};

const STATUS_COLORS = {
  active: '#89b4fa',
  enrolled: '#89b4fa',
  completed: '#a6e3a1',
  qualified: '#a6e3a1',
  removed: '#f38ba8',
  paused: '#f9e2af',
  blocked: '#f38ba8',
  draft: '#9399b2',
  running: '#89b4fa',
  RUNNING: '#89b4fa',
  COMPLETED: '#a6e3a1',
  FAILED: '#f38ba8',
  PENDING: '#f9e2af',
  NEW: '#9399b2',
};

const ACTIVITY_COLORS = {
  enrolled: '#89b4fa',
  removed: '#f38ba8',
  completed: '#a6e3a1',
  qualified: '#a6e3a1',
  email_sent: '#cba6f7',
  email_delivered: '#94e2d5',
  email_opened: '#f9e2af',
  email_clicked: '#fab387',
  email_bounced: '#f38ba8',
  email_replied: '#a6e3a1',
  step_failed: '#f38ba8',
  step_skipped: '#9399b2',
  step_executed: '#89b4fa',
};

/** Collapsible debug section */
const DebugSection = ({ title, badge, badgeColor, defaultOpen = false, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={SECTION_STYLE}>
      <div style={HEADER_STYLE} onClick={() => setOpen((o) => !o)}>
        <span>
          <Icon icon={open ? 'chevron-down' : 'chevron-right'} style={{ fontSize: 16, marginRight: 6 }} />
          {title}
          {badge != null && (
            <span style={{ ...BADGE_STYLE, background: badgeColor || '#45475a', color: '#cdd6f4' }}>{badge}</span>
          )}
        </span>
        <span style={{ color: '#6c7086', fontSize: 11 }}>{open ? 'collapse' : 'expand'}</span>
      </div>
      {open && <div style={BODY_STYLE}>{children}</div>}
    </div>
  );
};

DebugSection.propTypes = {
  title: PropTypes.string.isRequired,
  badge: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  badgeColor: PropTypes.string,
  defaultOpen: PropTypes.bool,
  children: PropTypes.node,
};

/** Pretty-printed JSON with syntax highlighting */
const JsonBlock = ({ data }) => {
  if (data === null || data === undefined) return <span style={{ color: '#6c7086' }}>null</span>;
  const json = typeof data === 'string' ? data : JSON.stringify(data, null, 2);
  return <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{json}</pre>;
};

JsonBlock.propTypes = { data: PropTypes.any };

/** Extract a human-readable error message from activity details */
const getErrorMessage = (activity) => {
  if (!activity.details) return null;
  const d = typeof activity.details === 'string' ? JSON.parse(activity.details) : activity.details;
  return d.error_message || d.reason || d.skip_reason || d.failure_reason || null;
};

/** Activity row */
const ActivityRow = ({ activity, events }) => {
  const color = ACTIVITY_COLORS[activity.activityType] || '#a6adc8';
  const eventLabel = events?.find((e) => String(e.id) === String(activity.cadenceEventId));
  const ts = activity.activityAt ? new Date(activity.activityAt).toLocaleString() : '—';
  const isError = ['step_failed', 'email_bounced', 'step_skipped'].includes(activity.activityType);
  const errorMsg = isError ? getErrorMessage(activity) : null;

  return (
    <div
      style={{
        padding: '6px 0',
        borderBottom: '1px solid #313244',
        display: 'grid',
        gridTemplateColumns: '180px 150px 1fr 100px',
        gap: 8,
        alignItems: 'start',
      }}
    >
      <span style={{ color: '#6c7086' }}>{ts}</span>
      <span style={{ color, fontWeight: 600 }}>{activity.activityType}</span>
      <span>
        <span style={{ color: activity.contactId ? '#a6adc8' : '#6c7086' }}>contact:{activity.contactId || '—'}</span>
        {eventLabel && (
          <span style={{ color: '#6c7086' }}>
            {' '}
            | step:{eventLabel.sequenceOrder} ({eventLabel.eventType})
          </span>
        )}
        {activity.emailSendId && <span style={{ color: '#6c7086' }}> | email:{activity.emailSendId}</span>}
        {errorMsg && <div style={{ color: '#f38ba8', fontSize: 11, marginTop: 2 }}>↳ {errorMsg}</div>}
        {!errorMsg && activity.details && (
          <span style={{ color: '#6c7086' }}> | {JSON.stringify(activity.details)}</span>
        )}
      </span>
      <span style={{ color: '#6c7086', textAlign: 'right' }}>m#{activity.cadenceMemberId || '—'}</span>
    </div>
  );
};

ActivityRow.propTypes = {
  activity: PropTypes.object.isRequired,
  events: PropTypes.array,
};

/** Tooltip for bounce/failure details on hover */
const FailureTooltip = ({ failureEvents }) => {
  if (!failureEvents || failureEvents.length === 0) return null;
  return (
    <div
      style={{
        position: 'absolute',
        bottom: '100%',
        left: 0,
        marginBottom: 4,
        background: '#313244',
        border: '1px solid #45475a',
        borderRadius: 6,
        padding: '8px 12px',
        fontSize: 11,
        color: '#cdd6f4',
        minWidth: 280,
        maxWidth: 420,
        zIndex: 10,
        boxShadow: '0 4px 12px rgba(0,0,0,.4)',
        whiteSpace: 'normal',
      }}
    >
      <div style={{ fontWeight: 600, color: '#f38ba8', marginBottom: 4 }}>Failures ({failureEvents.length})</div>
      {failureEvents.map((fe) => (
        <div key={fe.id} style={{ padding: '3px 0', borderBottom: '1px solid #45475a' }}>
          <span style={{ color: '#fab387' }}>{fe.failureType || 'unknown'}</span>
          {fe.failureReason && <span style={{ color: '#a6adc8' }}> — {fe.failureReason}</span>}
          {fe.failureDetails && (
            <div style={{ color: '#6c7086', marginTop: 2 }}>
              {typeof fe.failureDetails === 'string' ? fe.failureDetails : JSON.stringify(fe.failureDetails)}
            </div>
          )}
          {fe.triggeredAt && (
            <span style={{ color: '#585b70', fontSize: 10 }}> {new Date(fe.triggeredAt).toLocaleString()}</span>
          )}
        </div>
      ))}
    </div>
  );
};

FailureTooltip.propTypes = {
  failureEvents: PropTypes.array,
};

/** Member row with expandable activity */
const MemberRow = ({ member, events, cadenceId, cadenceStatus }) => {
  const [expanded, setExpanded] = useState(false);
  const [activity, setActivity] = useState(null);
  const [loadingActivity, setLoadingActivity] = useState(false);
  const [showFailures, setShowFailures] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [retryResult, setRetryResult] = useState(null); // null | 'ok' | error string

  const handleRetry = async (e) => {
    e.stopPropagation();
    setRetrying(true);
    setRetryResult(null);
    try {
      const data = await cadenceGqlCall(RETRY_BLOCKED_MEMBER_MUTATION, { memberId: String(member.id) });
      const res = data?.retryBlockedCadenceMember;
      if (res?.success) {
        setRetryResult('ok');
      } else {
        setRetryResult((res?.errors || []).join(', ') || 'failed');
      }
    } catch (err) {
      setRetryResult(err.message || 'failed');
    }
    setRetrying(false);
  };

  const loadActivity = useCallback(async () => {
    if (activity) {
      setExpanded((e) => !e);
      return;
    }
    setLoadingActivity(true);
    setExpanded(true);
    try {
      const data = await cadenceGqlCall(CADENCE_MEMBER_ACTIVITY_QUERY, {
        cadenceMemberId: String(member.id),
      });
      const conn = data.cadenceMemberActivity || {};
      setActivity((conn.edges || []).map((e) => e.node));
    } catch {
      setActivity([]);
    }
    setLoadingActivity(false);
  }, [member.id, activity]);

  const statusColor = STATUS_COLORS[member.status] || '#a6adc8';
  const hasFailures = member.failureEvents && member.failureEvents.length > 0;
  const hasRemovalReason = member.status === 'removed' && member.removalReason;

  // Detect incomplete member data (member query returned object but no real data)
  if (!member.id) {
    return (
      <div style={{ borderBottom: '1px solid #313244', padding: '8px 0', color: '#6c7086' }}>
        <span style={{ color: '#f38ba8', fontWeight: 600 }}>LOAD FAILED</span>
        {member._requestedId && <span> — requested id:{member._requestedId}</span>}
        {member.contactId && <span> — contact:{member.contactId}</span>}
        {member._loadError && <span style={{ color: '#f38ba8', marginLeft: 8 }}>({member._loadError})</span>}
      </div>
    );
  }

  return (
    <div style={{ borderBottom: '1px solid #313244' }}>
      <div
        style={{
          padding: '8px 0',
          display: 'grid',
          gridTemplateColumns: '60px 100px 100px 100px 80px 80px 80px 1fr',
          gap: 8,
          alignItems: 'center',
          cursor: 'pointer',
        }}
        onClick={loadActivity}
      >
        <span style={{ color: '#6c7086' }}>#{member.id}</span>
        <span style={{ color: member.contactId ? '#a6adc8' : '#6c7086' }}>
          {member.contactId ? `c:${member.contactId}` : 'c:—'}
        </span>
        <span style={{ color: statusColor, fontWeight: 600 }}>
          {member.status || '—'}
          {member.status === 'blocked' && (cadenceStatus || '').toLowerCase() === 'active' && (
            <button
              type="button"
              onClick={handleRetry}
              disabled={retrying}
              style={{
                marginLeft: 8,
                padding: '1px 7px',
                fontSize: 11,
                fontFamily: 'monospace',
                background: retrying ? '#45475a' : '#f38ba822',
                color: retrying ? '#6c7086' : '#f38ba8',
                border: '1px solid #f38ba8',
                borderRadius: 3,
                cursor: retrying ? 'not-allowed' : 'pointer',
              }}
            >
              {retrying ? 'retrying…' : 'retry'}
            </button>
          )}
          {retryResult && (
            <span
              style={{
                marginLeft: 6,
                fontSize: 11,
                color: retryResult === 'ok' ? '#a6e3a1' : '#f38ba8',
              }}
            >
              {retryResult === 'ok' ? '✓ queued' : `✗ ${retryResult}`}
            </span>
          )}
        </span>
        <span style={{ color: '#6c7086' }}>
          {member.enrolledAt ? new Date(member.enrolledAt).toLocaleDateString() : '—'}
        </span>
        <span>sent:{member.totalEmailsSent ?? 0}</span>
        <span>open:{member.totalEmailsOpened ?? 0}</span>
        <span>reply:{member.totalEmailsReplied ?? 0}</span>
        <span style={{ color: '#6c7086' }}>
          evt:{member.currentEventId || '—'}
          {member.nextEventScheduledAt && (
            <span style={{ color: '#f9e2af' }}> | next:{new Date(member.nextEventScheduledAt).toLocaleString()}</span>
          )}
          {member.isSuccessful && <span style={{ color: '#a6e3a1' }}> | SUCCESS</span>}
          {hasFailures && (
            <span
              style={{ position: 'relative', display: 'inline-block', marginLeft: 6 }}
              onMouseEnter={() => setShowFailures(true)}
              onMouseLeave={() => setShowFailures(false)}
            >
              <span
                style={{
                  color: '#f38ba8',
                  cursor: 'help',
                  fontWeight: 600,
                  padding: '1px 5px',
                  background: '#f38ba822',
                  borderRadius: 3,
                }}
              >
                {member.failureEvents.length} failure{member.failureEvents.length > 1 ? 's' : ''}
              </span>
              {showFailures && <FailureTooltip failureEvents={member.failureEvents} />}
            </span>
          )}
          {hasRemovalReason && <span style={{ color: '#f38ba8', marginLeft: 6 }}>removed: {member.removalReason}</span>}
        </span>
      </div>
      {expanded && (
        <div style={{ paddingLeft: 24, paddingBottom: 8 }}>
          {loadingActivity && <span style={{ color: '#6c7086' }}>Loading activity...</span>}
          {activity && activity.length === 0 && <span style={{ color: '#6c7086' }}>No activity recorded</span>}
          {activity && activity.map((a) => <ActivityRow key={a.id} activity={a} events={events} />)}
        </div>
      )}
    </div>
  );
};

MemberRow.propTypes = {
  member: PropTypes.object.isRequired,
  events: PropTypes.array,
  cadenceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  cadenceStatus: PropTypes.string,
};

/**
 * Secret debug/ops page for a cadence.
 * Access via /cadences-v2/:id/debug — not linked from any UI.
 * Shows raw data, activity log, jobs, members, stats, and event graph.
 */
const CadenceDebugPage = ({ cadenceId }) => {
  const [cadence, setCadence] = useState(null);
  const [stats, setStats] = useState(null);
  const [stepStats, setStepStats] = useState([]);
  const [goalProgress, setGoalProgress] = useState(null);
  const [members, setMembers] = useState([]);
  const [memberCount, setMemberCount] = useState(0);
  const [recentActivity, setRecentActivity] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [successEvalLog, setSuccessEvalLog] = useState(null);
  const [evaluatingSuccess, setEvaluatingSuccess] = useState(false);
  const [recalculating, setRecalculating] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    const id = String(cadenceId);

    try {
      const results = await Promise.allSettled([
        cadenceGqlCall(CADENCE_QUERY, { id }),
        cadenceGqlCall(CADENCE_STATS_QUERY, { cadenceId: id }),
        cadenceGqlCall(CADENCE_STEP_STATS_QUERY, { cadenceId: id }),
        cadenceGqlCall(CADENCE_GOAL_PROGRESS_QUERY, { cadenceId: id }),
        cadenceGqlCall(CADENCE_MEMBERS_QUERY, { cadenceId: id }),
        cadenceGqlCall(RECENT_CADENCE_ACTIVITY_QUERY, { cadenceId: id }),
        cadenceGqlCall(JOBS_QUERY, { cadenceId: id, limit: 20 }),
      ]);

      const [cadenceRes, statsRes, stepStatsRes, goalRes, membersRes, activityRes, jobsRes] = results;

      if (cadenceRes.status === 'fulfilled') setCadence(cadenceRes.value.cadence);
      if (statsRes.status === 'fulfilled') setStats(statsRes.value.cadenceStats);
      if (stepStatsRes.status === 'fulfilled') setStepStats(stepStatsRes.value.cadenceStepStats || []);
      if (goalRes.status === 'fulfilled') setGoalProgress(goalRes.value.cadenceGoalProgress);
      if (activityRes.status === 'fulfilled') {
        const actConn = activityRes.value.recentCadenceActivity || {};
        setRecentActivity((actConn.edges || []).map((e) => e.node));
      }
      if (jobsRes.status === 'fulfilled') setJobs(jobsRes.value.jobs || []);

      // Fetch individual members from cursors (same workaround as useCadenceMembers)
      if (membersRes.status === 'fulfilled') {
        const conn = membersRes.value.cadenceMembers;
        setMemberCount(conn?.totalCount || 0);
        const edges = conn?.edges || [];
        const memberIds = edges
          .map((e) => {
            if (e.node?.id) return String(e.node.id);
            if (e.cursor) {
              try {
                return atob(e.cursor);
              } catch {
                return null;
              }
            }
            return null;
          })
          .filter(Boolean);

        if (memberIds.length > 0) {
          const memberDetails = await Promise.allSettled(
            memberIds.map((mid) => cadenceGqlCall(CADENCE_MEMBER_QUERY, { id: String(mid) }))
          );
          const loadedMembers = memberDetails.map((r, idx) => {
            if (r.status === 'fulfilled' && r.value.cadenceMember) {
              return r.value.cadenceMember;
            }
            // Return a placeholder for failed loads so they're visible in the debug view
            return {
              id: null,
              contactId: null,
              status: null,
              _loadError: r.status === 'rejected' ? r.reason?.message : 'member query returned null',
              _requestedId: memberIds[idx],
            };
          });
          setMembers(loadedMembers);
        }
      }

      setLastRefresh(new Date());
    } catch (err) {
      setError(err.message);
    }

    setLoading(false);
  }, [cadenceId]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const handleReevaluateSuccess = async () => {
    setEvaluatingSuccess(true);
    setSuccessEvalLog(null);
    try {
      const data = await cadenceGqlCall(REEVALUATE_SUCCESS_METRICS_MUTATION, {
        cadenceId: String(cadenceId),
      });
      const res = data.reevaluateCadenceSuccessMetrics;
      setSuccessEvalLog(res);
      // Refresh data after re-evaluation
      fetchAll();
    } catch (err) {
      setSuccessEvalLog({ log: [`ERROR: ${err.message}`], evaluated: 0, newlySuccessful: 0 });
    }
    setEvaluatingSuccess(false);
  };

  const handleRecalculateStats = async () => {
    setRecalculating(true);
    try {
      await cadenceGqlCall(RECALCULATE_CADENCE_STATS_MUTATION, {
        cadenceId: String(cadenceId),
      });
      fetchAll();
    } catch (err) {
      setError(`Recalculate failed: ${err.message}`);
    }
    setRecalculating(false);
  };

  const pageStyle = {
    background: '#11111b',
    color: '#cdd6f4',
    minHeight: '100vh',
    padding: '16px 24px',
    fontFamily: 'monospace',
  };

  const topBarStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
    padding: '12px 16px',
    background: '#1e1e2e',
    borderRadius: 8,
    border: '1px solid #313244',
  };

  if (loading && !cadence) {
    return (
      <div style={pageStyle}>
        <div style={topBarStyle}>
          <span style={{ fontSize: 16, fontWeight: 700, color: '#f38ba8' }}>CADENCE DEBUG CONSOLE</span>
        </div>
        <Loading />
      </div>
    );
  }

  const statusColor = cadence ? STATUS_COLORS[cadence.status] || '#a6adc8' : '#6c7086';

  return (
    <div style={pageStyle}>
      {/* Top Bar */}
      <div style={topBarStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 16, fontWeight: 700, color: '#f38ba8' }}>DEBUG</span>
          <span style={{ color: '#cdd6f4', fontWeight: 600 }}>{cadence?.name || `Cadence #${cadenceId}`}</span>
          {cadence && (
            <span style={{ ...BADGE_STYLE, background: statusColor + '33', color: statusColor }}>{cadence.status}</span>
          )}
          <span style={{ color: '#6c7086', fontSize: 11 }}>
            id:{cadenceId} v:{cadence?.version}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {lastRefresh && (
            <span style={{ color: '#6c7086', fontSize: 11 }}>refreshed {lastRefresh.toLocaleTimeString()}</span>
          )}
          <Button type="secondary" onClick={handleRecalculateStats} disabled={recalculating}>
            {recalculating ? 'Recalculating...' : 'Recalc Stats'}
          </Button>
          <Button type="secondary" onClick={handleReevaluateSuccess} disabled={evaluatingSuccess}>
            {evaluatingSuccess ? 'Evaluating...' : 'Re-eval Success'}
          </Button>
          <Button type="secondary" onClick={fetchAll} disabled={loading}>
            <Icon icon="refresh" /> {loading ? 'Loading...' : 'Refresh'}
          </Button>
          <Button type="secondary" onClick={() => EverTrue.Navigator(`/cadences-v2/${cadenceId}`, true)}>
            Normal View
          </Button>
        </div>
      </div>

      {error && (
        <div style={{ padding: 12, background: '#f38ba833', borderRadius: 8, marginBottom: 16, color: '#f38ba8' }}>
          Error: {error}
        </div>
      )}

      {/* Success Metric Evaluation Log */}
      {successEvalLog && (
        <DebugSection
          title="SUCCESS METRIC EVALUATION LOG"
          badge={
            successEvalLog.newlySuccessful > 0
              ? `+${successEvalLog.newlySuccessful} newly successful`
              : `${successEvalLog.evaluated} evaluated`
          }
          badgeColor={successEvalLog.newlySuccessful > 0 ? '#a6e3a1' : '#45475a'}
          defaultOpen={true}
        >
          <pre
            style={{
              margin: 0,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              fontSize: 11,
              lineHeight: 1.6,
            }}
          >
            {(successEvalLog.log || []).map((line, i) => {
              // Color-code log lines
              let color = '#a6adc8';
              if (line.includes('NEWLY SUCCESSFUL')) color = '#a6e3a1';
              else if (line.includes('STILL SUCCESSFUL')) color = '#89b4fa';
              else if (line.includes('ALREADY SUCCESSFUL')) color = '#89b4fa';
              else if (line.includes('NOT SUCCESSFUL')) color = '#6c7086';
              else if (line.includes('WARNING')) color = '#f9e2af';
              else if (line.includes('ERROR') || line.includes('EXCEPTION')) color = '#f38ba8';
              else if (line.includes('=> True') || line.includes('=> true')) color = '#a6e3a1';
              else if (line.includes('=> False') || line.includes('=> false')) color = '#f38ba8';
              else if (line.startsWith('  metric[')) color = '#cba6f7';
              else if (line.startsWith('---')) color = '#89b4fa';
              else if (line.startsWith('===')) color = '#f9e2af';
              return (
                <span key={i} style={{ color }}>
                  {line}
                  {'\n'}
                </span>
              );
            })}
          </pre>
        </DebugSection>
      )}

      {/* Summary Cards */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
          gap: 12,
          marginBottom: 16,
        }}
      >
        {[
          { label: 'Members', value: memberCount, color: '#89b4fa' },
          { label: 'Active', value: stats?.activeMembers ?? '—', color: '#89b4fa' },
          { label: 'Completed', value: stats?.completedMembers ?? '—', color: '#a6e3a1' },
          { label: 'Qualified', value: stats?.qualifiedMembers ?? '—', color: '#a6e3a1' },
          { label: 'Removed', value: stats?.removedMembers ?? '—', color: '#f38ba8' },
          { label: 'Emails Sent', value: stats?.emailsSent ?? '—', color: '#cba6f7' },
          { label: 'Delivered', value: stats?.emailsDelivered ?? '—', color: '#94e2d5' },
          { label: 'Opened', value: stats?.emailsOpened ?? '—', color: '#f9e2af' },
          { label: 'Clicked', value: stats?.emailsClicked ?? '—', color: '#fab387' },
          { label: 'Bounced', value: stats?.emailsBounced ?? '—', color: '#f38ba8' },
          { label: 'Replied', value: stats?.emailsReplied ?? '—', color: '#a6e3a1' },
          { label: 'Events', value: cadence?.events?.length ?? '—', color: '#b4befe' },
        ].map((card) => (
          <div
            key={card.label}
            style={{
              background: '#1e1e2e',
              border: '1px solid #313244',
              borderRadius: 8,
              padding: '10px 14px',
              textAlign: 'center',
            }}
          >
            <div style={{ fontSize: 22, fontWeight: 700, color: card.color }}>{card.value}</div>
            <div style={{ fontSize: 11, color: '#6c7086', marginTop: 2 }}>{card.label}</div>
          </div>
        ))}
      </div>

      {/* Rates row */}
      {stats && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
            gap: 12,
            marginBottom: 16,
          }}
        >
          {[
            { label: 'Delivery Rate', value: stats.deliveryRate },
            { label: 'Open Rate', value: stats.openRate },
            { label: 'Click Rate', value: stats.clickRate },
            { label: 'Reply Rate', value: stats.replyRate },
            { label: 'Bounce Rate', value: stats.bounceRate },
          ].map((r) => (
            <div
              key={r.label}
              style={{
                background: '#1e1e2e',
                border: '1px solid #313244',
                borderRadius: 8,
                padding: '8px 14px',
                textAlign: 'center',
              }}
            >
              <div style={{ fontSize: 16, fontWeight: 600, color: '#cdd6f4' }}>
                {r.value != null ? `${Number(r.value).toFixed(1)}%` : '—'}
              </div>
              <div style={{ fontSize: 11, color: '#6c7086' }}>{r.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Bounced — prominent section when bounces exist */}
      {(() => {
        const bounced = recentActivity.filter((a) => a.activityType === 'email_bounced');
        if (bounced.length === 0 && (!stats?.emailsBounced || stats.emailsBounced === 0)) return null;
        return (
          <DebugSection
            title="BOUNCED"
            badge={stats?.emailsBounced ?? bounced.length}
            badgeColor="#f38ba8"
            defaultOpen={true}
          >
            {bounced.length === 0 ? (
              <span style={{ color: '#6c7086' }}>
                Stats report {stats?.emailsBounced} bounce(s) but no email_bounced activity records found in recent
                activity. The bounce may have occurred outside the recent activity window.
              </span>
            ) : (
              bounced.map((a) => {
                const details = a.details && typeof a.details === 'string' ? JSON.parse(a.details) : a.details || {};
                const reason = details.error_message || details.reason || details.bounce_reason || 'Unknown reason';
                const ts = a.activityAt ? new Date(a.activityAt).toLocaleString() : '—';
                return (
                  <div
                    key={a.id}
                    style={{
                      padding: '8px 0',
                      borderBottom: '1px solid #313244',
                      display: 'grid',
                      gridTemplateColumns: '180px 100px 1fr',
                      gap: 8,
                    }}
                  >
                    <span style={{ color: '#6c7086' }}>{ts}</span>
                    <span style={{ color: '#f38ba8', fontWeight: 600 }}>contact:{a.contactId || '—'}</span>
                    <span>
                      <div style={{ color: '#f38ba8' }}>↳ {reason}</div>
                      {a.cadenceEventId && (
                        <div style={{ color: '#6c7086', fontSize: 11 }}>
                          step:
                          {cadence?.events?.find((e) => String(e.id) === String(a.cadenceEventId))?.sequenceOrder ||
                            a.cadenceEventId}
                          {a.emailSendId && ` | email:${a.emailSendId}`} | member:#{a.cadenceMemberId || '—'}
                        </div>
                      )}
                    </span>
                  </div>
                );
              })
            )}
          </DebugSection>
        );
      })()}

      {/* Recent Activity */}
      <DebugSection
        title="RECENT ACTIVITY"
        badge={recentActivity.length}
        badgeColor={recentActivity.length > 0 ? '#89b4fa' : '#45475a'}
        defaultOpen={true}
      >
        {recentActivity.length === 0 && <span style={{ color: '#6c7086' }}>No activity recorded yet</span>}
        {recentActivity.map((a) => (
          <ActivityRow key={a.id} activity={a} events={cadence?.events} />
        ))}
      </DebugSection>

      {/* Members */}
      <DebugSection title="MEMBERS" badge={`${members.length}/${memberCount}`} badgeColor="#89b4fa">
        {members.length === 0 && (
          <span style={{ color: '#6c7086' }}>No members (click to expand individual activity)</span>
        )}
        <div
          style={{
            fontSize: 11,
            color: '#6c7086',
            marginBottom: 8,
            borderBottom: '1px solid #313244',
            paddingBottom: 4,
          }}
        >
          <div style={{ display: 'grid', gridTemplateColumns: '60px 100px 100px 100px 80px 80px 80px 1fr', gap: 8 }}>
            <span>ID</span>
            <span>Contact</span>
            <span>Status</span>
            <span>Enrolled</span>
            <span>Sent</span>
            <span>Opened</span>
            <span>Replied</span>
            <span>Current Event / Next Scheduled</span>
          </div>
        </div>
        {members.map((m, idx) => (
          <MemberRow
            key={m.id || `failed-${idx}`}
            member={m}
            events={cadence?.events}
            cadenceId={cadenceId}
            cadenceStatus={cadence?.status}
          />
        ))}
      </DebugSection>

      {/* Background Jobs */}
      <DebugSection
        title="BACKGROUND JOBS"
        badge={jobs.length}
        badgeColor={jobs.some((j) => j.status === 'RUNNING' || j.status === 'PENDING') ? '#f9e2af' : '#45475a'}
      >
        {jobs.length === 0 && <span style={{ color: '#6c7086' }}>No jobs found for this cadence</span>}
        {jobs.map((job) => {
          const jobColor = STATUS_COLORS[job.status] || '#a6adc8';
          return (
            <div key={job.id} style={{ padding: '8px 0', borderBottom: '1px solid #313244' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ color: '#6c7086' }}>#{job.id}</span>
                <span style={{ color: jobColor, fontWeight: 600 }}>{job.status}</span>
                <span>{job.jobType}</span>
                <span style={{ color: '#6c7086' }}>
                  {job.processedItems}/{job.totalItems} ({job.progress}%)
                </span>
                {job.completedAt && (
                  <span style={{ color: '#6c7086' }}>completed {new Date(job.completedAt).toLocaleString()}</span>
                )}
                {job.errorMessage && <span style={{ color: '#f38ba8' }}>error: {job.errorMessage}</span>}
              </div>
            </div>
          );
        })}
      </DebugSection>

      {/* Event Graph */}
      <DebugSection title="EVENT GRAPH" badge={cadence?.events?.length}>
        {cadence?.events?.map((event, i) => {
          const stepStepStat = stepStats.find((s) => String(s.cadenceEventId) === String(event.id));
          return (
            <div key={event.id} style={{ padding: '8px 0', borderBottom: '1px solid #313244' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ color: '#6c7086', width: 30 }}>#{event.sequenceOrder}</span>
                <span style={{ color: '#b4befe', fontWeight: 600, width: 100 }}>{event.eventType}</span>
                <span style={{ color: event.isActive ? '#a6e3a1' : '#f38ba8', width: 60 }}>
                  {event.isActive ? 'active' : 'inactive'}
                </span>
                <span
                  style={{
                    color:
                      event.eventType?.toLowerCase() === 'task' ? '#f9e2af' : event.isAutomated ? '#89b4fa' : '#f9e2af',
                    width: 80,
                  }}
                >
                  {event.eventType?.toLowerCase() === 'task' ? 'manual' : event.isAutomated ? 'automated' : 'manual'}
                </span>
                <span style={{ color: '#6c7086' }}>
                  id:{event.id} ref:{event.referenceId}
                </span>
                {event.emailTemplateId && <span style={{ color: '#cba6f7' }}>tpl:{event.emailTemplateId}</span>}
              </div>
              {stepStepStat && (
                <div style={{ paddingLeft: 42, fontSize: 11, marginTop: 4 }}>
                  <span style={{ color: '#6c7086' }}>
                    executed:{stepStepStat.totalExecuted} skipped:{stepStepStat.totalSkipped} sent:
                    {stepStepStat.emailsSent} delivered:{stepStepStat.emailsDelivered} opened:
                    {stepStepStat.emailsOpened} clicked:{stepStepStat.emailsClicked} bounced:
                    {stepStepStat.emailsBounced} qualified:{stepStepStat.membersQualified}
                  </span>
                  {stepStepStat.errorCount > 0 && (
                    <span style={{ color: '#f38ba8', fontWeight: 600, marginLeft: 8 }}>
                      errors:{stepStepStat.errorCount}
                    </span>
                  )}
                  {stepStepStat.errorReasons && Object.keys(stepStepStat.errorReasons).length > 0 && (
                    <div style={{ paddingLeft: 0, marginTop: 2, color: '#f38ba8' }}>
                      {Object.entries(stepStepStat.errorReasons).map(([reason, count]) => (
                        <span key={reason} style={{ marginRight: 12 }}>
                          {reason}:{count}
                        </span>
                      ))}
                    </div>
                  )}
                  {stepStepStat.skippedReasons && Object.keys(stepStepStat.skippedReasons).length > 0 && (
                    <div style={{ paddingLeft: 0, marginTop: 2, color: '#9399b2' }}>
                      skip reasons:{' '}
                      {Object.entries(stepStepStat.skippedReasons).map(([reason, count]) => (
                        <span key={reason} style={{ marginRight: 12 }}>
                          {reason}:{count}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {event.eventConfig && (
                <div style={{ paddingLeft: 42, color: '#585b70', fontSize: 11, marginTop: 2 }}>
                  config: {JSON.stringify(event.eventConfig)}
                </div>
              )}
            </div>
          );
        })}

        {cadence?.edges?.length > 0 && (
          <div style={{ marginTop: 12, paddingTop: 8, borderTop: '1px solid #45475a' }}>
            <div style={{ color: '#6c7086', marginBottom: 6, fontWeight: 600 }}>EDGES</div>
            {cadence.edges.map((edge) => (
              <div key={edge.id} style={{ padding: '4px 0', color: '#6c7086' }}>
                {edge.fromCadenceEventId} → {edge.toCadenceEventId}{' '}
                <span style={{ color: '#b4befe' }}>({edge.edgeType})</span>
              </div>
            ))}
          </div>
        )}
      </DebugSection>

      {/* Success Metrics & Auto-Removal Rules */}
      <DebugSection title="SUCCESS METRICS & AUTO-REMOVAL RULES">
        <div style={{ marginBottom: 12 }}>
          <div style={{ color: '#a6e3a1', fontWeight: 600, marginBottom: 6 }}>Success Metrics</div>
          {(!cadence?.successMetrics || cadence.successMetrics.length === 0) && (
            <span style={{ color: '#6c7086' }}>None configured</span>
          )}
          {cadence?.successMetrics?.map((m) => (
            <div key={m.id} style={{ padding: '4px 0', borderBottom: '1px solid #313244' }}>
              <span style={{ color: '#a6e3a1' }}>{m.metricType}</span>
              <span style={{ color: '#6c7086' }}>
                {' '}
                target:{m.targetPercentage}% priority:{m.priority} order:{m.priorityOrder}
              </span>
              {m.metricConfig && <span style={{ color: '#585b70' }}> config:{JSON.stringify(m.metricConfig)}</span>}
              {m.terminalEvent && (
                <span style={{ color: '#cba6f7' }}>
                  {' '}
                  → terminal:{m.terminalEvent.eventType} (id:{m.terminalEvent.id})
                </span>
              )}
            </div>
          ))}
        </div>
        <div>
          <div style={{ color: '#f38ba8', fontWeight: 600, marginBottom: 6 }}>Auto-Removal Rules</div>
          {(!cadence?.autoRemovalRules || cadence.autoRemovalRules.length === 0) && (
            <span style={{ color: '#6c7086' }}>None configured</span>
          )}
          {cadence?.autoRemovalRules?.map((r) => (
            <div key={r.id} style={{ padding: '4px 0', borderBottom: '1px solid #313244' }}>
              <span style={{ color: '#f38ba8' }}>{r.removalType}</span>
              <span style={{ color: '#6c7086' }}> priority:{r.priority}</span>
              {r.removalConfig && <span style={{ color: '#585b70' }}> config:{JSON.stringify(r.removalConfig)}</span>}
              {r.terminalEvent && (
                <span style={{ color: '#cba6f7' }}>
                  {' '}
                  → terminal:{r.terminalEvent.eventType} (id:{r.terminalEvent.id})
                </span>
              )}
            </div>
          ))}
        </div>
      </DebugSection>

      {/* Goal Progress */}
      <DebugSection title="GOAL PROGRESS">
        {goalProgress ? (
          <JsonBlock data={goalProgress} />
        ) : (
          <span style={{ color: '#6c7086' }}>No goal configured</span>
        )}
      </DebugSection>

      {/* Raw Cadence JSON */}
      <DebugSection title="RAW CADENCE DATA">
        <JsonBlock data={cadence} />
      </DebugSection>

      {/* Raw Stats JSON */}
      <DebugSection title="RAW STATS DATA">
        <JsonBlock data={stats} />
      </DebugSection>

      {/* Step Stats */}
      <DebugSection title="RAW STEP STATS" badge={stepStats.length}>
        <JsonBlock data={stepStats} />
      </DebugSection>
    </div>
  );
};

CadenceDebugPage.propTypes = {
  cadenceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default CadenceDebugPage;
